import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  Row,
  Col,
  Table,
  Form,
  FormGroup,
  Input,
  Button,
} from "reactstrap";

import { API_BASE_URL } from "../config/api";
import { ItemOrderControls, ItemOrderMessage, useItemOrdering } from "../components/ItemOrderControls";

const BASE_URL = API_BASE_URL;
const createEmptyUpdateImages = () => ({ image_path_en: null, image_path_ar: null });

const resolveImageUrl = (imagePath) => {
  if (!imagePath) return "";
  if (/^https?:\/\//i.test(imagePath)) return imagePath;
  return `${BASE_URL}/${imagePath}`;
};

function Announcements() {
  const [items, setItems] = useState([]);
  const [newImageEn, setNewImageEn] = useState(null);
  const [newImageAr, setNewImageAr] = useState(null);
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [count, setCount] = useState(0);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [updateItem, setUpdateItem] = useState({
    id: null,
    link_url: "",
    image_path: "",
    image_path_en: "",
    image_path_ar: "",
  });
  const [updateImages, setUpdateImages] = useState(createEmptyUpdateImages);
  const [updateError, setUpdateError] = useState("");
  const [updateSuccessMessage, setUpdateSuccessMessage] = useState("");
  const { moveItem, orderMessage, savingOrder } = useItemOrdering(items, setItems, `${BASE_URL}/announcements/order`);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newImageEn || !newImageAr) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image_path_en", newImageEn);
      formData.append("image_path_ar", newImageAr);
      formData.append("link_url", newLinkUrl);

      await axios.post(`${BASE_URL}/announcements/add`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      fetchItems();
      setNewImageEn(null);
      setNewImageAr(null);
      setNewLinkUrl("");
      const enInput = document.getElementById("newAnnouncementImageEn");
      const arInput = document.getElementById("newAnnouncementImageAr");
      if (enInput) enInput.value = "";
      if (arInput) arInput.value = "";
    } catch (err) {
      console.error("Error adding announcement:", err);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/announcements/list`);
      const nextItems = response.data.data || [];
      setItems(nextItems);
      setCount(nextItems.length);
    } catch (err) {
      console.error(`Error fetching announcements: ${err}`);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/announcements/delete/${id}`);
      setItems((prev) => prev.filter((item) => item.id !== id));
      setCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error deleting announcement:", err);
    }
  };

  const handleUpdate = (item) => {
    setUpdateItem({
      id: item.id,
      link_url: item.link_url || "",
      image_path: item.image_path || "",
      image_path_en: item.image_path_en || item.image_path || "",
      image_path_ar: item.image_path_ar || item.image_path || "",
    });
    setUpdateImages(createEmptyUpdateImages());
    setUpdateError("");
    setUpdateSuccessMessage("");
    setShowUpdateForm(true);
  };

  const handleUpdateChange = (e) => {
    const { name, value } = e.target;
    setUpdateItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateImageChange = (fieldName, file) => {
    setUpdateImages((prev) => ({
      ...prev,
      [fieldName]: file || null,
    }));
  };

  const handleAnnouncementUpdate = async (e) => {
    e.preventDefault();
    if (!updateItem.id) return;

    try {
      const formData = new FormData();
      formData.append("link_url", updateItem.link_url);
      if (updateImages.image_path_en) {
        formData.append("image_path_en", updateImages.image_path_en);
      }
      if (updateImages.image_path_ar) {
        formData.append("image_path_ar", updateImages.image_path_ar);
      }

      await axios.put(`${BASE_URL}/announcements/update/${updateItem.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUpdateSuccessMessage("Announcement updated successfully");
      setTimeout(() => setUpdateSuccessMessage(""), 4000);
      setShowUpdateForm(false);
      fetchItems();
    } catch (err) {
      console.error("Error updating announcement:", err);
      setUpdateError("Error updating announcement");
      setTimeout(() => setUpdateError(""), 4000);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  return (
    <>
      <div className="content">
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md="6">
              <FormGroup>
                <label>English Image:</label>
                <Input id="newAnnouncementImageEn" type="file" onChange={(e) => setNewImageEn(e.target.files?.[0] || null)} />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <label>Arabic Image:</label>
                <Input id="newAnnouncementImageAr" type="file" onChange={(e) => setNewImageAr(e.target.files?.[0] || null)} />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <label>External Link:</label>
                <Input type="text" value={newLinkUrl} onChange={(e) => setNewLinkUrl(e.target.value)} />
              </FormGroup>
            </Col>
            <Col md="12">
              <Button className="btn-round" color="primary" type="submit">
                Add Announcement
              </Button>
            </Col>
          </Row>
        </Form>

        <CardFooter>
          <div>Total announcements: {count}</div>
        </CardFooter>

        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Announcements</CardTitle>
              </CardHeader>
              <CardBody>
                <ItemOrderMessage message={orderMessage} />
                <Table responsive>
                  <thead className="text-primary">
                    <tr>
                      <th>Image (EN)</th>
                      <th>Image (AR)</th>
                      <th>External Link</th>
                      <th>Order</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={item.id}>
                        <td>
                          {item.image_path_en && (
                            <img
                              src={resolveImageUrl(item.image_path_en)}
                              alt="Announcement EN"
                              style={{ width: "200px", height: "200px", objectFit: "cover" }}
                            />
                          )}
                        </td>
                        <td>
                          {item.image_path_ar && (
                            <img
                              src={resolveImageUrl(item.image_path_ar)}
                              alt="Announcement AR"
                              style={{ width: "200px", height: "200px", objectFit: "cover" }}
                            />
                          )}
                        </td>
                        <td>{item.link_url || ""}</td>
                        <td><ItemOrderControls index={index} count={items.length} onMove={moveItem} disabled={savingOrder} /></td>
                        <td>
                          <Button color="success" onClick={() => handleUpdate(item)} style={{ marginRight: "8px" }}>
                            Update
                          </Button>
                          <Button color="danger" onClick={() => handleDelete(item.id)}>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      {showUpdateForm && (
        <div className="content">
          <Form onSubmit={handleAnnouncementUpdate}>
            <Row>
              <Col md="7">
                <FormGroup>
                  <label>External Link:</label>
                  <Input
                    type="text"
                    name="link_url"
                    value={updateItem.link_url}
                    onChange={handleUpdateChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup>
                  <label>Current English Image:</label>
                  {updateItem.image_path_en && (
                    <div>
                      <img
                        src={resolveImageUrl(updateItem.image_path_en)}
                        alt="Current Announcement EN"
                        style={{ maxWidth: "200px", marginBottom: "10px" }}
                      />
                    </div>
                  )}
                  <label>New English Image (optional):</label>
                  <Input type="file" onChange={(e) => handleUpdateImageChange("image_path_en", e.target.files?.[0] || null)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <label>Current Arabic Image:</label>
                  {updateItem.image_path_ar && (
                    <div>
                      <img
                        src={resolveImageUrl(updateItem.image_path_ar)}
                        alt="Current Announcement AR"
                        style={{ maxWidth: "200px", marginBottom: "10px" }}
                      />
                    </div>
                  )}
                  <label>New Arabic Image (optional):</label>
                  <Input type="file" onChange={(e) => handleUpdateImageChange("image_path_ar", e.target.files?.[0] || null)} />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="12">
                <div className="update ml-auto mr-auto">
                  <Button className="btn-round" color="primary" type="submit">
                    Update Announcement
                  </Button>{" "}
                  <Button
                    className="btn-round"
                    color="secondary"
                    onClick={() => {
                      setShowUpdateForm(false);
                      setUpdateItem({ id: null, link_url: "", image_path: "", image_path_en: "", image_path_ar: "" });
                      setUpdateImages(createEmptyUpdateImages());
                    }}
                  >
                    Cancel
                  </Button>
                  {updateError && <div style={{ color: "red", marginTop: "10px" }}>{updateError}</div>}
                  {updateSuccessMessage && (
                    <div className="alert alert-success" role="alert" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
                      {updateSuccessMessage}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      )}
    </>
  );
}

export default Announcements;
